import { ProjectPermissions } from "@/types/post";
import { QuestionType, Scaling } from "@/types/question";
import { VoteDirection } from "@/types/votes";

export type AuthorType = {
  id: number;
  username: string;
  is_bot: boolean;
  is_staff: boolean;
};

export type BECommentType = {
  id: number;
  author: AuthorType;
  author_staff_permission: ProjectPermissions | null;
  on_post: number;
  root_id: number | null;
  parent_id: number | null;
  created_at: string;
  text_edited_at: string;
  is_soft_deleted: boolean;
  text: string;
  included_forecast?: ForecastType;
  is_private: boolean;
  vote_score?: number;
  user_vote: VoteDirection;
  changed_my_mind: {
    for_this_user: boolean;
    count: number;
  };
  mentioned_users: AuthorType[];
  on_post_data?: {
    id: number;
    title: string;
  };
  is_current_content_translated?: boolean;
  key_factors?: KeyFactor[];
  key_factor_votes_score?: number;
  is_pinned: boolean;
};

export type CommentType = BECommentType & {
  children: CommentType[];
};

export type CommentOfWeekEntry = {
  votes_score: number;
  changed_my_mind_count: number;
  key_factor_votes_score: number;
  excluded: boolean;
  comment: BECommentType;
};

export type ForecastType = {
  start_time: Date;
  probability_yes: number;
  probability_yes_per_category: (number | null)[];
  options: string[];
  continuous_cdf: number[];
  quartiles: [number, number, number];
  scaling: Scaling;
  question_type: QuestionType;
  question_unit?: string;
};

export enum KeyFactorVoteTypes {
  STRENGTH = "strength",
  DIRECTION = "direction",
}

export enum StrengthValues {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 5,
  NO_IMPACT = 0,
}

export type KeyFactorVoteType =
  (typeof KeyFactorVoteTypes)[keyof typeof KeyFactorVoteTypes];

export type StrengthVoteOption = 0 | 1 | 2 | 5;

export type KeyFactorVoteScore = -5 | -3 | -2 | -1 | 0 | 1 | 2 | 3 | 5 | null;

export type KeyFactorVote = {
  vote_type: KeyFactorVoteType;
  score: KeyFactorVoteScore;
  show_second_step?: boolean; // used only for two step survey
  second_step_completed?: boolean; // used only for two step survey
};

export enum ImpactDirectionCategory {
  Increase,
  Decrease,
  More,
  Less,
  Earlier,
  Later,
  IncreaseUncertainty,
}

export type ImpactMetadata = {
  impact_direction: 1 | -1 | null;
  certainty: -1 | null;
};

export type ImpactDirection = "increase" | "decrease" | "uncertainty";

export type Driver = ImpactMetadata & {
  text: string;
};

export type BaseRate = {
  type: "frequency" | "trend";
  reference_class: string;

  rate_numerator?: number | null;
  rate_denominator?: number | null;

  projected_value?: number | null;
  projected_by_year?: number | null;

  unit: string;
  extrapolation?: "" | "linear" | "exponential" | "other";
  based_on?: string;
  source: string;
};

export type News = ImpactMetadata & {
  url: string;
  title: string;
  img_url?: string;
  source: string;
  published_at?: string;
};

export type KeyFactor = {
  id: number;
  driver?: Driver | null;
  base_rate?: BaseRate | null;
  news?: News | null;
  author: AuthorType; // used to set limit per question
  comment_id: number;
  created_at: string;
  vote: KeyFactorVoteAggregate;
  question_id?: number | null;
  question?: {
    id: number;
    label: string;
    unit?: string | null;
  } | null;
  question_option?: string;
  freshness: number;
  post: {
    id: number;
    unit?: string;
    question_type?: QuestionType;
  };
  flagged_by_me?: boolean;
};

export type KeyFactorVoteReason = "wrong_direction" | "no_impact" | "redundant";

export type KeyFactorVoteAggregate = {
  // Aggregated strength score
  score: number;
  // Current user's vote
  user_vote: KeyFactorVoteScore;
  // Current user's downvote reason
  user_vote_reason: KeyFactorVoteReason | null;
  // Total number of votes
  count: number;
  aggregated_data: { score: number; count: number }[];
};

type DraftBase = {
  markdown: string;
  lastModified: number;
  userId: number;
};

export type CreateDraft = DraftBase & {
  kind: "create";
  postId: number;
  parentId?: number;
  includeForecast: boolean;
};

export type EditDraft = DraftBase & {
  kind: "edit";
  commentId: number;
  onPostId?: number;
  isPrivate?: boolean;
};

export type Draft = CreateDraft | EditDraft;
